/**
 * mSTEM Save Project Modal (PRJ-03 / LLD UC 08). Presentational component
 * — Redux wiring lives in src/containers/save-project-modal.jsx.
 *
 * Single form field per LLD UC 08:
 *   1. Tên dự án (required, validate non-empty + unique within
 *      mstem.myProjects[]) — defaults to current project title on mount.
 *
 * 2 buttons:
 *   - Hủy bỏ (secondary)
 *   - Lưu (primary navy)
 *
 * Validation strings live in this file alongside the input render so the
 * Phase 3 aggregated i18n test (test/unit/i18n/phase-3-messages.test.js)
 * can pick them up by id during extraction.
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './save-project-modal.css';

// NEW IDs unique to this plan (PRJ-03) — extracted by babel react-intl plugin
// since they live inside defineMessages().
const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Lưu dự án',
        description: 'Title of Save Project modal (PRJ-03, LLD UC 08)',
        id: 'gen.project.save.title'
    },
    confirmSave: {
        defaultMessage: 'Lưu',
        description: 'Confirm save button label (PRJ-03)',
        id: 'gen.project.save.confirm'
    }
});

// REUSED IDs from Plan 03-02 new-project-modal — declared as plain object
// literals (NOT inside defineMessages) so babel react-intl plugin SKIPS
// them during extraction. This avoids the openblock-l10n
// build-i18n-src "Duplicate message id with different content" guard
// (which fires when the same id appears in two files with non-identical
// description strings). intl.formatMessage() and <FormattedMessage> both
// accept any {id, defaultMessage} descriptor shape at runtime — locale
// catalog lookup hits the existing entry registered by Plan 03-02.
// Mirrors the Plan 03-03 TYPE_LABEL_IDS pattern.
const reused = {
    fieldName: {
        defaultMessage: 'Tên dự án',
        id: 'gen.project.field.name'
    },
    cancel: {
        defaultMessage: 'Hủy bỏ',
        id: 'gen.project.action.cancel'
    },
    errNameRequired: {
        defaultMessage: 'Vui lòng nhập tên dự án',
        id: 'gen.project.error.nameRequired'
    },
    errNameDuplicate: {
        defaultMessage: 'Đã có dự án trùng tên. Vui lòng chọn tên khác',
        id: 'gen.project.error.nameDuplicate'
    },
    errNameTooLong: {
        defaultMessage: 'Tên dự án tối đa 100 ký tự',
        id: 'gen.project.error.nameTooLong'
    }
};

const SaveProjectModalComponent = ({
    intl, name, error,
    onChangeName, onBlurName, onSubmit, onCancel
}) => (
    <Modal
        className={styles.modalContent}
        contentLabel={intl.formatMessage(messages.modalTitle)}
        onRequestClose={onCancel}
    >
        <div className={styles.body}>
            <form
                className={styles.editForm}
                noValidate
                onSubmit={onSubmit}
            >
                <div className={styles.formField}>
                    <label
                        className={styles.formLabel}
                        htmlFor="sp-name"
                    >
                        <FormattedMessage {...reused.fieldName} />
                        <span className={styles.required}>{'*'}</span>
                    </label>
                    <input
                        autoFocus
                        aria-describedby={error ? 'sp-name-error' : null}
                        aria-invalid={Boolean(error)}
                        className={classNames(styles.formInput, {
                            [styles.formInputError]: error
                        })}
                        id="sp-name"
                        maxLength={100}
                        type="text"
                        value={name}
                        onBlur={onBlurName}
                        onChange={onChangeName}
                    />
                    {error ? (
                        <span
                            className={styles.formError}
                            id="sp-name-error"
                        >
                            <FormattedMessage {...reused[error]} />
                        </span>
                    ) : null}
                </div>

                <div className={styles.formActions}>
                    <button
                        className={styles.btnSecondary}
                        type="button"
                        onClick={onCancel}
                    >
                        <FormattedMessage {...reused.cancel} />
                    </button>
                    <button
                        className={styles.btnPrimary}
                        type="submit"
                    >
                        <FormattedMessage {...messages.confirmSave} />
                    </button>
                </div>
            </form>
        </div>
    </Modal>
);

SaveProjectModalComponent.propTypes = {
    error: PropTypes.string,
    intl: intlShape.isRequired,
    name: PropTypes.string.isRequired,
    onBlurName: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChangeName: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
};

SaveProjectModalComponent.defaultProps = {
    error: null
};

export default injectIntl(SaveProjectModalComponent);
